import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Container, Row, Column, VideoModal, Icon } from '@pella/sash-framework';
import { Layout, SEO, Breadcrumbs, CTA, Image, Video } from '../../components';
import { initDataLayer } from '../../components/utilities/functions';
import { isValidArray } from '../../components/utilities/validations';
import ImageBanner from '../../components/common/image-banner';
import SlickSlider from '../../components/common/slick-slider';
import CardSection from '../../components/common/card-section';
import HeroSimple from '../../components/common/hero-simple';
import Feature from '../../components/common/feature';
import styles from './index.module.scss';

const Difference = (props) => {
	const pageData = props.data.contentstackDifference;
	const [video, setVideo] = useState(false);

	const showDuration = (duration) => {
		const splitDuration = duration.split('PT')[1].split('M');
		const minute = splitDuration[0];
		const seconds = splitDuration[1].split('S')[0];

		return (
			<p className={styles.videoDuration}>
				{minute}:{seconds}
			</p>
		);
	};

	return (
		<Layout
			id='template-Difference'
			pageTitle={pageData.seo?.title}
		>
			{pageData.breadcrumb && (
				<Breadcrumbs
					data={pageData.breadcrumb}
					currentUrl={pageData.url}
				/>
			)}

			{pageData.hero && <HeroSimple data={pageData.hero} />}

			{pageData.top_content?.heading && (
				<div className={styles.diffIntro}>
					<Container width={1200}>
						<div className={styles.diffIntro_Paragraph}>
							<p>{pageData.top_content.heading}</p>
						</div>
					</Container>
				</div>
			)}

			{pageData.top_content?.anchor_content?.[0] && (
				<Container width={1800}>
					<Row
						justify='between'
						verticalGutters={true}
						className={styles.diffIntro_Row}
					>
						{pageData.top_content.anchor_content.map((anchor, index) => (
							<Column
								xs={12}
								md={4}
								key={index}
							>
								{anchor.heading && <h3>{anchor.heading}</h3>}
								{anchor.description && (
									<div
										dangerouslySetInnerHTML={{
											__html: anchor.description,
										}}
									/>
								)}
								{isValidArray(anchor?.ctas) && (
									<CTA
										data={anchor.ctas[0]}
										className={styles.diffIntro_AnchorLink}
									/>
								)}
							</Column>
						))}
					</Row>
				</Container>
			)}

			{isValidArray(pageData?.modular_blocks) &&
				pageData.modular_blocks.map((block, index) => {
					if (block.Banner_copy_below != null) {
						return (
							<div
								key={index}
								id={block.Banner_copy_below.anchor_text || null}
								className={styles.singleImage_Wrapper}
							>
								{block.Banner_copy_below.banner_image && block.Banner_copy_below.banner_image[0] && (
									<Image
										data={block.Banner_copy_below.banner_image[0]}
										className={styles.singleImage_Image}
									/>
								)}

								{block.Banner_copy_below.content && (
									<div className={styles.singleImage_ContentWrapper}>
										<Container width={1200}>
											{block.Banner_copy_below.content.heading && <h2 className={`${styles.sectionTitle} s2-h2__alt`}>{block.Banner_copy_below.content.heading}</h2>}
											{block.Banner_copy_below.content.subheading && (
												<div
													className={styles.singleImage_Subtitle}
													dangerouslySetInnerHTML={{
														__html: block.Banner_copy_below.content.subheading,
													}}
												/>
											)}
											{block.Banner_copy_below.content.description && (
												<div
													className={styles.singleImage_Content}
													dangerouslySetInnerHTML={{
														__html: block.Banner_copy_below.content.description,
													}}
												/>
											)}
										</Container>
									</div>
								)}
							</div>
						);
					}

					if (isValidArray(block?.Columns?.column)) {
						return (
							<div
								key={index}
								style={{
									marginBottom: +block.Columns.spacing_below,
									marginTop: +block.Columns.spacing_above,
								}}
							>
								<Row
									noGutters={true}
									align='center'
								>
									{block.Columns.column.map((col, index) => (
										<Column
											key={index}
											xs={col.column_mobile}
											lg={col.column_desktop}
											className={col.image && col.image[0] ? styles.diffColumns_ImageCol : ''}
										>
											{col.image && col.image[0] && (
												<Image
													data={col.image[0]}
													className={styles.diffColumns_Image}
												/>
											)}
											{(col.subheading || col.heading || col.description) && (
												<div className={styles.diffColumns_ContentWrapper}>
													{col.subheading && <p className={styles.diffColumns_Subtitle}>{col.subheading}</p>}
													{col.heading && <h3>{col.heading}</h3>}
													{col.description && (
														<div
															dangerouslySetInnerHTML={{
																__html: col.description,
															}}
															className={styles.diffColumns_Description}
														/>
													)}
													{isValidArray(col?.ctas) && <CTA data={col.ctas[0]} />}
												</div>
											)}
										</Column>
									))}
								</Row>
							</div>
						);
					}

					if (block.Just_Copy != null) {
						return (
							<div
								key={index}
								id={block.Just_Copy.anchor_text || null}
							>
								<Container width={1200}>
									{block.Just_Copy.heading && <h2 className={`${styles.sectionTitle} s2-h2__alt`}>{block.Just_Copy.heading}</h2>}
									{block.Just_Copy.description && (
										<div
											dangerouslySetInnerHTML={{
												__html: block.Just_Copy.description,
											}}
											className={styles.sectionDescription}
										/>
									)}
									{block.Just_Copy.quote && (
										<div
											className={styles.diffCopy_Quote}
											dangerouslySetInnerHTML={{
												__html: block.Just_Copy.quote,
											}}
										/>
									)}
								</Container>
							</div>
						);
					}

					if (isValidArray(block?.video?.video)) {
						return (
							<div
								key={index}
								id={block.video.anchor_text || null}
								className={styles.diffVideo}
							>
								<Container width={1200}>
									{block.video.heading && <h2 className={`${styles.sectionTitle} s2-h2__alt`}>{block.video.heading}</h2>}
									{block.video.description && (
										<div
											dangerouslySetInnerHTML={{
												__html: block.video.description,
											}}
											className={styles.diffVideo_Description}
										/>
									)}
									<Video data={block.video.video[0]} />
								</Container>
							</div>
						);
					}

					if (block.Video_Carousel != null) {
						return (
							<div
								key={index}
								id={block.Video_Carousel.anchor_text || null}
								className={`${styles.sectionWrapper} ${styles.diffTested}`}
							>
								<div className={styles.diffTested_Intro}>
									<Container width={1200}>
										{block.Video_Carousel.heading && <h2 className={`${styles.sectionTitle} s2-h2__alt`}>{block.Video_Carousel.heading}</h2>}
										{block.Video_Carousel.subheading && <p className={styles.diffTested_Subtitle}>{block.Video_Carousel.subheading}</p>}
									</Container>
								</div>

								{isValidArray(block?.Video_Carousel?.videos) && (
									<SlickSlider
										slidesToShow={1}
										slidesToScroll={1}
										dots={true}
										draggable={false}
										dotsClass={styles.diffTested_SliderDots}
										className={styles.diffTested_Slider}
										lazyLoad={false}
										initialSlide={1}
										customPaging={(i) => <button>{block.Video_Carousel.videos[i].heading}</button>}
										responsive={[
											{
												breakpoint: 790,
												settings: {
													dots: false,
													initialSlide: 0,
												},
											},
										]}
									>
										{block.Video_Carousel.videos.map((slide, index) => {
											const imageCheck = isValidArray(slide?.video) && isValidArray(slide.video[0].image_overlay);
											return (
												<div
													className={styles.diffTested_Slide}
													key={index}
												>
													<Container
														width={1200}
														noPadding={true}
														fluid
													>
														<div
															className={styles.diffTested_SliderImage}
															style={{
																backgroundImage: `url('${imageCheck && slide.video[0].image_overlay[0].image.url}')`,
															}}
														>
															{isValidArray(slide?.video) && slide.video[0].video && slide.video[0].video.video_id && (
																<button
																	onClick={() => {
																		setVideo(slide.video[0].video.video_id);
																		initDataLayer('videoplay');
																	}}
																>
																	<span className={styles.srOnly}>Watch the Video</span>
																	<div className={styles.pdTested_SliderIcon}>
																		<Icon name='play_arrow' />
																	</div>
																</button>
															)}
															{slide.video[0].schema && slide.video[0].schema.duration && showDuration(slide.video[0].schema.duration)}
														</div>

														<div className={styles.diffTested_SliderContent}>
															{slide.heading && <h3>{slide.heading}</h3>}
															{slide.description && (
																<div
																	dangerouslySetInnerHTML={{
																		__html: slide.description,
																	}}
																/>
															)}
														</div>
													</Container>

													{isValidArray(slide?.video) && (
														<VideoModal
															videoId={slide.video[0].video.video_id}
															title={slide.video[0].video.title}
															onClose={() => setVideo(false)}
															isOpen={video === slide.video[0].video.video_id}
															transcript={slide.video[0].transcript}
														/>
													)}
												</div>
											);
										})}
									</SlickSlider>
								)}

								{isValidArray(block?.Video_Carousel?.ctas) && (
									<Container
										width={1200}
										className={styles.diffTested_ButtonWrapper}
									>
										<CTA data={block.Video_Carousel.ctas[0]} />
									</Container>
								)}
							</div>
						);
					}

					if (block.image_aside != null && block.image_aside.callout) {
						return (
							<ImageBanner
								data={block.image_aside}
								key={index}
							/>
						);
					}

					if (block.image_aside != null && !block.image_aside.callout) {
						return (
							<Feature
								data={{ feature: [block.image_aside] }}
								key={index}
							/>
						);
					}

					if (block.cards != null) {
						return (
							<CardSection
								key={index}
								data={block.cards}
							/>
						);
					}
				})}
		</Layout>
	);
};

Difference.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
};

export default Difference;

// eslint-disable-next-line react/prop-types -- fix this
export const Head = ({ data }) => <SEO {...data.contentstackDifference.seo} />;

export const query = graphql`
	query ($uid: String!) {
		contentstackDifference(id: { eq: $uid }) {
			url
			breadcrumb {
				active_page
				back_pages {
					...CTA
				}
			}
			hero {
				image {
					...Image
					image {
						gatsbyImageData(width: 1500, quality: 90)
					}
				}
				body
			}
			top_content {
				heading
				anchor_content {
					heading
					description
					ctas {
						...CTA
					}
				}
			}
			modular_blocks {
				video {
					heading
					description
					video {
						...Video
					}
				}
				Banner_copy_below {
					anchor_text
					banner_image {
						...Image
						image {
							gatsbyImageData(width: 1920, layout: FULL_WIDTH, quality: 90)
						}
					}
					content {
						heading
						subheading
						description
					}
				}
				Just_Copy {
					anchor_text
					heading
					description
					quote
				}
				Video_Carousel {
					anchor_text
					heading
					subheading
					videos {
						video {
							...Video
						}
						heading
						description
					}
					ctas {
						...CTA
					}
				}
				Columns {
					spacing_below
					spacing_above
					column {
						column_desktop
						column_mobile
						image {
							...Image
							image {
								gatsbyImageData(height: 650, quality: 90)
							}
						}
						subheading
						heading
						description
						ctas {
							...CTA
						}
					}
				}
				cards {
					...CardSection
				}
				image_aside {
					...ImageAside
				}
			}
			seo {
				title
				description
				noIndex: no_index_page
			}
		}
	}
`;
